import { Button, Divider, Link, Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useEffect } from "react";
import { useDestructiveConfirm } from "src/utilities";
import {
  ActionItemContextProperty,
  ActionItemFeed,
  ActionItemProperties,
  DrawerLayout,
  IssueDrawerContent,
  ITeamSection,
  ItemDescriptionField,
  ItemDrawerTitle,
  SignalRGroup,
  SnackbarContent,
  TaskItemDrawerContent,
  useDrawer,
} from "..";
import { ActionItemPropertyType, Links, SupportingItemType } from "../../constants";
import { useSignalR } from "../../hooks";
import { queryKeys, useDeleteActionItem, useEditActionItem, useGetActionItemDetail, UserDto } from "../../http";
import { ITeamSummary } from "../../models";

interface IProps {
  actionItemGuid: string;
  onOwnershipChanged?: (actionItemGuid: string, actionItemDescription: string, removedOwners: UserDto[]) => void;
}

const ActionItemDrawerContent = (props: IProps) => {
  const { isLoading, data: actionItem } = useGetActionItemDetail(props.actionItemGuid);
  const { mutate: editActionItem } = useEditActionItem();
  const { mutate: deleteActionItem } = useDeleteActionItem();
  const { showDrawer, closeDrawer } = useDrawer();
  const { enqueueSnackbar } = useSnackbar();
  const destructiveConfirm = useDestructiveConfirm();
  const signalR = useSignalR();
  const queryClient = useQueryClient();

  useEffect(() => {
    signalR.joinGroup(SignalRGroup.ActionItem(props.actionItemGuid));

    return () => {
      signalR.leaveGroup(SignalRGroup.ActionItem(props.actionItemGuid));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.actionItemGuid]);

  useEffect(() => {
    const handleShouldRefetchActionItem = (actionItemGuid: string) => {
      queryClient.invalidateQueries(queryKeys.actionItems.detail(actionItemGuid));
    };

    signalR.onShouldRefetchActionItem(handleShouldRefetchActionItem);
    return () => signalR.offShouldRefetchActionItem(handleShouldRefetchActionItem);
  }, [queryClient, signalR]);

  function handleDescriptionChanged(value: string) {
    if (actionItem === undefined) {
      return;
    }

    editActionItem({
      actionItemGuid: actionItem.guid,
      description: value,
      planGuid: actionItem.teamPlanSection.planGuid,
    });
  }

  function handleTeamChanged(selectedTeam: ITeamSummary | null, selectedSection: ITeamSection | null) {
    if (actionItem === undefined) {
      return;
    }

    if (!selectedSection) {
      enqueueSnackbar(<SnackbarContent title="Item removed from team." />, {
        action: (
          <Button href={Links.ActionItemDetail(actionItem.guid)} sx={{ whiteSpace: "nowrap" }}>
            Open Page
          </Button>
        ),
      });
      closeDrawer();
    } else if (actionItem.teamPlanSection.teamSlug !== selectedTeam?.slug) {
      enqueueSnackbar(
        <SnackbarContent
          title={
            <>
              <span>Item moved to </span>
              <Link onClick={(e) => e.stopPropagation()} href={Links.TeamStrategicMap(selectedTeam!.slug)}>
                {selectedTeam!.name}
              </Link>
              .
            </>
          }
        />,
        {
          action: (
            <Button href={Links.ActionItemDetail(actionItem.guid)} sx={{ whiteSpace: "nowrap" }}>
              Open Page
            </Button>
          ),
        },
      );
      closeDrawer();
    }
  }

  function getDrawerContentForSupportingItem(itemType: SupportingItemType, itemGuid: string): React.ReactNode {
    switch (itemType) {
      case SupportingItemType.ActionItem:
        return <ActionItemDrawerContent actionItemGuid={itemGuid} />;
      case SupportingItemType.TaskItem:
        return <TaskItemDrawerContent taskItemGuid={itemGuid} />;
      case SupportingItemType.Issue:
        return <IssueDrawerContent issueGuid={itemGuid} />;
      default:
        return null;
    }
  }

  function showSupportingItemDrawer(itemType: SupportingItemType, itemGuid: string) {
    showDrawer({
      content: getDrawerContentForSupportingItem(itemType, itemGuid),
    });
  }

  async function handleDeleteClicked() {
    if (actionItem === undefined) {
      return;
    }

    const { confirmed } = await destructiveConfirm({
      title: "Delete",
      content: "Are you sure you want to delete this strategic item?",
      confirmationText: "Delete",
      cancellationText: "Cancel",
    });

    if (!confirmed) {
      return;
    }

    closeDrawer();
    deleteActionItem({ actionItemGuid: actionItem.guid, planGuid: actionItem.teamPlanSection.planGuid });
  }

  return (
    <DrawerLayout
      title={
        <ItemDrawerTitle link={Links.ActionItemDetail(props.actionItemGuid)} onDeleteClicked={handleDeleteClicked} />
      }
      subtitle={
        <>
          {!isLoading && (actionItem?.context || actionItem?.userCanEdit) && (
            <ActionItemContextProperty actionItem={actionItem} isEditable={actionItem.userCanEdit} linksOpenDrawer />
          )}

          <ItemDescriptionField
            isLoading={isLoading}
            isEditable={actionItem?.userCanEdit ?? false}
            value={actionItem?.description}
            onChange={handleDescriptionChanged}
            variant="h5"
          />
        </>
      }
    >
      <Stack divider={<Divider flexItem />}>
        <ActionItemProperties
          isLoading={isLoading}
          actionItem={actionItem}
          propertyVisibility={{
            [ActionItemPropertyType.Notes]: true,
            [ActionItemPropertyType.Ownership]: true,
            [ActionItemPropertyType.Status]: true,
            [ActionItemPropertyType.DueDate]: true,
            [ActionItemPropertyType.SupportingItems]: actionItem?.teamPlanSection != null,
            [ActionItemPropertyType.Metrics]: true,
            [ActionItemPropertyType.AzureDevOps]: true,
          }}
          onTeamChanged={handleTeamChanged}
          onOwnershipChanged={props.onOwnershipChanged}
          onSupportingItemClicked={showSupportingItemDrawer}
        />
        <ActionItemFeed actionItemGuid={props.actionItemGuid} />
      </Stack>
    </DrawerLayout>
  );
};

export { ActionItemDrawerContent };
